import { useDialog } from "@/client/stores/dialog";
import {
  Alert,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { CheckCircleIcon, XCircleIcon, ClockIcon } from "@phosphor-icons/react";
import { useWithdrawal } from "@/client/services/withdrawals/withdrawal";
import { useValidate, useReject } from "@/client/services/withdrawals/validate-reject";
import { useState } from "react";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  DÉTAIL D'UN RETRAIT — décision admin
 * ─────────────────────────────────────────────────────────────────────────────
 *  Règles appliquées :
 *   - le rejet exige un MOTIF (le serveur refuse une note vide) ;
 *   - l'action destructive est en deux temps (révéler → confirmer) ;
 *   - mutations nominatives : le serveur enregistre decidedById.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const MEAN_LABELS: Record<string, string> = {
  MOBILE_MONEY: "Mobile Money",
  BANK_TRANSACTION: "Virement bancaire",
};

export const GetWithdrawalDialog = () => {
  const { isOpen, close, payload } = useDialog<{ id: string }>("get-withdrawal");
  const { withdrawal } = useWithdrawal(payload?.item?.id ?? "");
  const { validate, loading: validating, error: validateError } = useValidate();
  const { reject, loading: rejecting, error: rejectError } = useReject();
  const [note, setNote] = useState("");
  const [confirmingReject, setConfirmingReject] = useState(false);

  const resetAndClose = () => {
    setNote("");
    setConfirmingReject(false);
    close();
  };

  const onValidate = async () => {
    if (!withdrawal) return;
    await validate({ id: withdrawal.id, note: note.trim() || undefined });
    resetAndClose();
  };

  const onReject = async () => {
    if (!withdrawal) return;
    await reject({ id: withdrawal.id, note: note.trim() });
    resetAndClose();
  };

  if (withdrawal) {
    return (
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Details du retrait</DialogTitle>
          </DialogHeader>
          <div className="grid-row-5 grid grid-cols-2 gap-2">
            <div className="border-secondary border-b-2 font-bold">Reference</div>
            <div className="border-secondary border-b-2"> {withdrawal.reference} </div>
            <div className="border-secondary border-b-2 font-bold">Montant</div>
            <div className="border-secondary border-b-2">{withdrawal.amount}</div>
            <div className="border-secondary border-b-2 font-bold">Ambassadeur</div>
            <div className="border-secondary border-b-2">{withdrawal.user?.name}</div>
            <div className="border-secondary border-b-2 font-bold">Canal de paiement</div>
            <div className="border-secondary border-b-2">
              {MEAN_LABELS[withdrawal.mean ?? "MOBILE_MONEY"] ?? withdrawal.mean}
              {(
                withdrawal.mean === "BANK_TRANSACTION"
                  ? withdrawal.bankNumber
                  : withdrawal.phoneMomo
              )
                ? ` — ${withdrawal.mean === "BANK_TRANSACTION" ? withdrawal.bankNumber : withdrawal.phoneMomo}`
                : ""}
            </div>
            <div className="border-secondary border-b-2 font-bold">Date de demande</div>
            <div className="border-secondary border-b-2">
              {new Date(withdrawal.demandDate).toDateString()}
            </div>
            <div className="border-secondary border-b-2 font-bold">Date de décision</div>
            <div className="border-secondary border-b-2">
              {withdrawal.decisionDate
                ? new Date(withdrawal.decisionDate).toDateString()
                : "Non définie"}
            </div>

            <div className="border-secondary border-b-2 font-bold">Statut</div>
            <div className="border-secondary border-b-2">
              {withdrawal.status === "accepted" && <Badge variant="success">Accepté</Badge>}
              {withdrawal.status === "rejected" && <Badge variant="error">Rejeté</Badge>}
              {withdrawal.status === "pending" && <Badge variant="warning">En attente</Badge>}
            </div>

            {withdrawal.status !== "pending" && (
              <>
                <div className="border-secondary border-b-2 font-bold">Décision prise par</div>
                <div className="border-secondary border-b-2">
                  {withdrawal.decidedBy?.name ?? "—"}
                </div>
                <div className="border-secondary border-b-2 font-bold">Motif</div>
                <div className="border-secondary border-b-2">{withdrawal.decisionNote ?? "—"}</div>
              </>
            )}
          </div>
          {withdrawal.status === "pending" && (
            <div className="flex flex-col gap-3">
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                placeholder="Note de décision — obligatoire pour un rejet"
                className="border-secondary bg-background text-foreground focus-visible:ring-primary w-full rounded-md border p-3 text-sm outline-none focus-visible:ring-2"
              />

              {(validateError || rejectError) && (
                <p className="text-sm text-red-600">
                  La décision n'a pas pu être enregistrée. Réessayez.
                </p>
              )}

              <div className="flex justify-center gap-x-3">
                <Button
                  className="bg-moncvpro-secondary"
                  disabled={validating || rejecting}
                  onClick={onValidate}
                >
                  {validating ? "Validation…" : "Valider"}
                </Button>

                {confirmingReject ? (
                  <Button
                    variant="error"
                    className="rounded-lg"
                    disabled={rejecting || note.trim().length === 0}
                    onClick={onReject}
                  >
                    {rejecting ? "Rejet…" : "Confirmer le rejet"}
                  </Button>
                ) : (
                  <Button
                    variant="error"
                    className="rounded-lg"
                    disabled={rejecting}
                    onClick={() => setConfirmingReject(true)}
                  >
                    Rejeter
                  </Button>
                )}
              </div>

              {confirmingReject && note.trim().length === 0 && (
                <p className="text-center text-xs text-amber-600">
                  Un motif est requis pour confirmer le rejet.
                </p>
              )}
            </div>
          )}
        </DialogContent>
        <DialogFooter></DialogFooter>
      </Dialog>
    );
  }
};
